import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { Toaster } from "react-hot-toast";
import { containerFor, initAnalytics } from "./lib/analytics";
import { ApiError, sessionQuery } from "./lib/api";
import { routeTree } from "./routes";
import "./styles.css";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 30_000,
			refetchOnWindowFocus: true,
			retry: 1,
		},
		mutations: { retry: false },
	},
});

// FR-I-4. Once, before anything renders — `track` queues onto dataLayer, so an
// event fired before the async container lands is not lost, but the
// `environment` push has to be the FIRST thing on the queue.
initAnalytics(
	containerFor(import.meta.env.VITE_GTM_ID, import.meta.env.VITE_ENVIRONMENT),
	import.meta.env.VITE_ENVIRONMENT,
);

const router = createRouter({
	routeTree,
	context: { queryClient },
	defaultPreload: "intent",
	scrollRestoration: true,
});

/**
 * The partner session is a cookie with a finite life, and a tab left open
 * overnight will get a 401 on its next fetch. Handled once, here, so no page
 * has to dead-end on "could not load your account" when the real answer is
 * "sign in again".
 */
function handleExpiredSession(error: unknown) {
	if (!(error instanceof ApiError) || !error.isSessionGone) return;
	queryClient.setQueryData(sessionQuery.queryKey, { authenticated: false });
	router.navigate({ to: "/login" });
}

queryClient.getQueryCache().config.onError = handleExpiredSession;
queryClient.getMutationCache().config.onError = handleExpiredSession;

const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
	ReactDOM.createRoot(rootElement).render(
		<StrictMode>
			<QueryClientProvider client={queryClient}>
				<RouterProvider router={router} />
				<Toaster
					position="top-center"
					toastOptions={{
						duration: 4000,
						style: {
							background: "#2B2B2B",
							color: "#F8F6F3",
							fontSize: "16px",
						},
					}}
				/>
			</QueryClientProvider>
		</StrictMode>,
	);
}
